import { Collapse, type CollapseProps } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackForBusinessLinkClicked } from "../../../analytics/amplitudeTracking";
import { Link } from "../../../router/LocaleLink";
import styles from "../StockScreener/stockScreener.module.css";
import { ETF_FAQ_KEYS, ETF_QUICK_LINKS } from "./etfSeoLinks";

/**
 * Crawlable help text, links and FAQ under the table. The FAQ uses the same
 * keys as the FAQPage JSON-LD (src/seo/structuredData.ts), so they match.
 */
export const EtfScreenerSeoContent = () => {
  const { t } = useTranslation();
  const p = "pages.etfScreener.seoContent.";
  const [expanded, setExpanded] = useState(false);

  const faqItems: CollapseProps["items"] = useMemo(
    () =>
      ETF_FAQ_KEYS.map((key) => ({
        key,
        label: <span className={styles.seoFaqQuestion}>{t(`${p}faq.${key}.q`)}</span>,
        children: <p className={styles.seoFaqAnswer}>{t(`${p}faq.${key}.a`)}</p>,
        // Keep answers in the DOM so crawlers read them without clicking.
        forceRender: true,
      })),
    [p, t],
  );

  return (
    <footer className={styles.seoFooter}>
      <section className={styles.seoSection} aria-labelledby="etf-help-heading">
        <h2 id="etf-help-heading" className={styles.seoHeading}>
          {t(`${p}helpTitle`)}
        </h2>
        {/* Collapse by whole paragraphs, never by height: a fixed max-height
            cuts a line in half. The rest stays in the HTML (hidden) so
            crawlers still read it. */}
        <p className={styles.seoParagraph}>{t(`${p}helpParagraph2`)}</p>
        <div hidden={!expanded} className={styles.seoTextExpanded}>
          <p className={styles.seoParagraph}>{t(`${p}disagreeParagraph`)}</p>
          <p className={styles.seoParagraph}>{t(`${p}coverageParagraph`)}</p>
          <h3 className={styles.seoSubheading}>{t(`${p}quickLinksTitle`)}</h3>
          <ul className={styles.seoLinkList}>
            {ETF_QUICK_LINKS.map(({ key, path }) => (
              <li key={key} className={styles.seoLinkItem}>
                <Link
                  to={path}
                  className={styles.seoLink}
                  onClick={
                    key === "forBusiness"
                      ? () =>
                          trackForBusinessLinkClicked({
                            link_location: "etf_screener_seo",
                            destination: path,
                          })
                      : undefined
                  }
                >
                  {t(`${p}links.${key}`)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <button
          type="button"
          className={styles.seoToggleBtn}
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? t(`${p}showLess`) : t(`${p}showMore`)}
        </button>
      </section>

      <section className={styles.seoSection} aria-labelledby="etf-faq-heading">
        <h2 id="etf-faq-heading" className={styles.seoHeading}>
          {t(`${p}faqTitle`)}
        </h2>
        <p className={styles.seoParagraphMuted}>{t(`${p}faqSubtitle`)}</p>
        <Collapse
          className={styles.seoFaqCollapse}
          bordered={false}
          expandIconPosition="end"
          items={faqItems}
        />
      </section>
    </footer>
  );
};
